import React from 'react';
import { useNavigate } from 'react-router-dom';
import { IntelligenceShell, MonoNum } from '../components/intelligence/IntelligenceShell';
import { useIntelligenceReport, LiveBadge, CARD } from '../components/intelligence/ReportKit';
import { INSIGHT_META, CAMPAIGN_LINK_FOR_KIND } from '../components/intelligence/AiSuggestion';
import { useOperatorLocale } from '../hooks/useOperatorLocale';
import { Anchor, TrendingUp, CalendarRange, Users, ArrowRight, Sparkles, Filter, Globe, Megaphone, Send, Receipt } from 'lucide-react';
import { canIntelTab } from '../utils/intelPerms';

const REPORTS = [
  {
    id: 'asset-earnings',
    title: 'Asset Earnings',
    desc: 'Every asset in your fleet, ranked by what it actually earned — revenue, booked hours and rev/hour.',
    icon: Anchor,
    path: '/intelligence/asset-earnings',
  },
  {
    id: 'service-sales-mix',
    title: 'Revenue Mix',
    desc: 'Which of your services sell, what customers really pay, add-on attach rates and booking lead times.',
    icon: TrendingUp,
    path: '/intelligence/service-sales-mix',
  },
  {
    id: 'demand-calendar',
    title: 'Demand Center',
    desc: 'When your customers book and when they show up — by day, start time and how far in advance.',
    icon: CalendarRange,
    path: '/intelligence/demand-calendar',
  },
  {
    id: 'customer-insights',
    title: 'Customer Insights',
    desc: 'Who books with you — group sizes, repeat rate, and your highest-value customers.',
    icon: Users,
    path: '/intelligence/customer-insights',
  },
  {
    id: 'expenses',
    title: 'Expense Tracker',
    desc: 'Where your money goes — spend by category, asset and crew, tracked against the revenue it supports.',
    icon: Receipt,
    path: '/intelligence/expenses',
  },
  {
    id: 'booking-funnel',
    title: 'Booking Funnel',
    desc: 'Every step of your booking widget — where visitors drop off, by service, price and device.',
    icon: Filter,
    path: '/intelligence/booking-funnel',
  },
  {
    id: 'website-analytics',
    title: 'Website Analytics',
    desc: 'Your own pixel — pageviews, visitors, referrers and how website traffic turns into bookings.',
    icon: Globe,
    path: '/intelligence/website-analytics',
  },
  {
    id: 'audiences',
    title: 'Audiences',
    desc: 'Every reachable contact you have earned — emails, verified phones, opt-ins and export-ready segments.',
    icon: Megaphone,
    path: '/intelligence/audiences',
  },
  {
    id: 'campaigns',
    title: 'Campaigns',
    desc: 'Turn your audiences into repeat bookings — AI-drafted email, SMS and WhatsApp drips with full ROI attribution.',
    icon: Send,
    path: '/intelligence/campaigns',
  },
];

const InsightCard = ({ insight, delay }) => {
  const { formatMoney } = useOperatorLocale();
  const navigate = useNavigate();
  const meta = INSIGHT_META[insight.kind];
  if (!meta) return null;
  const Icon = meta.icon;
  const projected = `+${formatMoney(insight.projectedAnnual)}/yr`;
  const text = meta.copy(insight.params, formatMoney).replace('{v}', projected);
  const link = CAMPAIGN_LINK_FOR_KIND[insight.kind];
  return (
    <div className={`${CARD} p-5 flex items-start gap-4 intel-rise`} style={{ animationDelay: `${delay}ms` }} data-testid={`intel-insight-${insight.kind}`}>
      <div className="w-10 h-10 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center shrink-0">
        <Icon className="w-[18px] h-[18px]" strokeWidth={1.75} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3 mb-1">
          <span className="font-bold text-sm text-slate-900">{meta.title}</span>
          <MonoNum className="text-sm font-extrabold text-[#2563EB] whitespace-nowrap">{projected}</MonoNum>
        </div>
        <p className="text-sm text-slate-500 leading-relaxed">{text}</p>
        {link && (
          <button
            data-testid={`intel-insight-campaign-link-${insight.kind}`}
            onClick={() => navigate(`/intelligence/campaigns?create=1&goal=${link.goal}&segment=${link.segment}`)}
            className="mt-2.5 inline-flex items-center gap-1.5 text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8]"
          >
            <Send className="w-3.5 h-3.5" /> {link.label} <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default function IntelligenceHome() {
  const navigate = useNavigate();
  const { formatMoney } = useOperatorLocale();
  const { data } = useIntelligenceReport('service-sales-mix', 12);
  const { data: insightsData } = useIntelligenceReport('insights', 12);
  const s = data?.summary;
  const insights = insightsData?.insights || [];

  return (
    <IntelligenceShell>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10" data-testid="intelligence-home">
        <div className="mb-8 intel-rise">
          <div className="flex items-center gap-2.5 mb-3">
            <LiveBadge />
            <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-400">Built from your bookings only</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Overview</h1>
        </div>

        {/* Last-12-months snapshot */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {[
            { label: 'Revenue · 12 mo', value: s ? formatMoney(s.totalRevenue) : '—', testid: 'intel-home-kpi-revenue' },
            { label: 'Bookings · 12 mo', value: s ? s.totalBookings.toLocaleString() : '—', testid: 'intel-home-kpi-bookings' },
            { label: 'Avg party size', value: s ? (s.avgPartySize ?? '—') : '—', testid: 'intel-home-kpi-party' },
            { label: 'Avg lead time', value: s ? (s.avgLeadDays != null ? `${s.avgLeadDays} days` : '—') : '—', testid: 'intel-home-kpi-lead' },
          ].map((k, i) => (
            <div key={k.label} className={`${CARD} p-4 sm:p-5 intel-rise`} style={{ animationDelay: `${60 + i * 60}ms` }} data-testid={k.testid}>
              <div className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">{k.label}</div>
              <MonoNum className="text-lg sm:text-2xl font-extrabold tracking-tight leading-tight text-[#2563EB]">{k.value}</MonoNum>
            </div>
          ))}
        </div>

        {/* Predictive opportunities */}
        {insights.length > 0 && (
          <div className="mb-10" data-testid="intel-opportunities-section">
            <div className="mb-5 intel-rise" style={{ animationDelay: '280ms' }}>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#2563EB]" />
                <h2 className="text-base font-extrabold tracking-tight text-slate-900">Opportunities</h2>
              </div>
              <p className="text-xs text-slate-400 mt-1 font-semibold">Directional estimates from your last 12 months — your data only, not guarantees.</p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {insights.map((ins, i) => (
                <InsightCard key={ins.kind} insight={ins} delay={320 + i * 70} />
              ))}
            </div>
          </div>
        )}

        <div className="mb-5 intel-rise" style={{ animationDelay: '300ms' }}>
          <h2 className="text-base font-extrabold tracking-tight text-slate-900">Chartroom</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {REPORTS.filter((r) => canIntelTab(r.id)).map((r, i) => {
            const Icon = r.icon;
            return (
              <button
                key={r.id}
                data-testid={`intelligence-report-card-${r.id}`}
                onClick={() => navigate(r.path)}
                className={`group text-left ${CARD} p-6 hover:shadow-[0_12px_36px_-12px_rgba(37,99,235,0.3)] transition-shadow duration-200 cursor-pointer intel-rise`}
                style={{ animationDelay: `${340 + i * 70}ms` }}
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-11 h-11 rounded-full bg-[#EBF2FF] text-[#2563EB] flex items-center justify-center">
                    <Icon className="w-5 h-5" strokeWidth={1.75} />
                  </div>
                  <LiveBadge />
                </div>
                <div className="font-bold mb-1.5 text-slate-900">{r.title}</div>
                <p className="text-sm leading-relaxed text-slate-500">{r.desc}</p>
                <div className="mt-5 flex items-center gap-1.5 text-sm font-bold text-[#2563EB]">
                  Open report <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </IntelligenceShell>
  );
}
